import { isGalleryArtworkLoadQuotaEnabled } from "../../utils/galleryLoadQuota";

export type CollaboratorRole = "owner" | "editor" | "contributor" | null;

type CollaboratorEntry = { userId: string; email: string; role: "editor" | "contributor" };

export type ViewerContext = {
  admin: boolean;
  isAdminAsSeller: boolean;
  personal: boolean;
  /** True if user can change title, mood, sound, spawning point, publish, and manage collaborators */
  canManageGallery: boolean;
  /** Resolved role: owner, editor, contributor, or null */
  collaboratorRole: CollaboratorRole;
};

export const PSEUDO_MOM_SUBTYPE = "pseudoMoM";
const PLATFORM_ADMIN_ROLES = ["GALLERY", "ADMIN", "SUPERADMIN"];

type SpaceEditContract = {
  type?: string;
  subtype?: string;
} | null | undefined;

type GalleryQuotaContract = {
  spaceType?: string;
} | null | undefined;

type GalleryIntroContract = {
  data?: {
    pseudoMomIntroDurationSeconds?: unknown;
  };
} | null | undefined;

type MoodPersistenceResponse<T> = {
  status?: boolean;
  moodIndex?: unknown;
  mood?: T;
  moods?: T[];
} | null | undefined;

type CachedPublicGalleryContract = {
  galleryId?: string | null;
} | null | undefined;

type GalleryModalKeyboardState = {
  artworksModal?: boolean;
  buyArtworkModal?: boolean;
  createArtworkModal?: boolean;
  gallerySetupModal?: boolean;
  feedbackModalOpen?: boolean;
  enquireModal?: boolean;
  confirmDeleteHexagon?: boolean;
  confirmOpenHexagon?: boolean;
  renderSettingsModal?: boolean;
};

export const isGalleryKeyboardBlocked = (state: GalleryModalKeyboardState): boolean => Boolean(
  state.artworksModal ||
  state.buyArtworkModal ||
  state.createArtworkModal ||
  state.gallerySetupModal ||
  state.feedbackModalOpen ||
  state.enquireModal ||
  state.confirmDeleteHexagon ||
  state.confirmOpenHexagon
);

export const resolveGalleryKeyboardAvailability = (
  requestedAvailability: boolean,
  modalBlocked: boolean
): boolean => requestedAvailability && !modalBlocked;

export const isPseudoMoMSpace = (space: SpaceEditContract): boolean => {
  return space?.type === "custom" && space?.subtype === PSEUDO_MOM_SUBTYPE;
};

export const resolveGalleryIntroDurationSeconds = (
  space: SpaceEditContract,
  gallery: GalleryIntroContract
): number => {
  const rawDuration = gallery?.data?.pseudoMomIntroDurationSeconds;
  if (rawDuration !== null && rawDuration !== undefined && rawDuration !== "") {
    const configuredDuration = Number(rawDuration);
    if (Number.isFinite(configuredDuration) && configuredDuration >= 0) {
      return configuredDuration;
    }
  }
  return isPseudoMoMSpace(space) ? 5 : 0;
};

export const shouldRunGalleryIntro = (
  space: SpaceEditContract,
  gallery: GalleryIntroContract
): boolean => {
  if (isPseudoMoMSpace(space)) return true;

  return resolveGalleryIntroDurationSeconds(space, gallery) > 0;
};

export const resolveSavedMoodPersistence = <T>(
  currentMoods: T[] | undefined,
  requestedMoodIndex: number,
  requestedMood: T,
  response: MoodPersistenceResponse<T>
): { moodIndex: number; mood: T; moods: T[] } => {
  const responseMoodIndex = Number(response?.moodIndex);
  const moodIndex = Number.isInteger(responseMoodIndex) && responseMoodIndex >= 0
    ? responseMoodIndex
    : requestedMoodIndex;
  const mood = response?.mood ?? requestedMood;

  if (Array.isArray(response?.moods)) {
    return { moodIndex, mood, moods: [...response.moods] };
  }

  const moods = Array.isArray(currentMoods) ? [...currentMoods] : [];
  moods[moodIndex] = mood;
  return { moodIndex, mood, moods };
};

export const shouldShowGalleryStatsPanel = (
  personal: boolean,
  space: SpaceEditContract,
  gallery?: GalleryQuotaContract
): boolean => {
  return Boolean(
    personal &&
    space?.type !== "open" &&
    isGalleryArtworkLoadQuotaEnabled(gallery, space)
  );
};

export const shouldPausePseudoMoMModelRotation = (anyModalOpen: boolean, isMovingArtworks: boolean): boolean => {
  return Boolean(anyModalOpen || isMovingArtworks);
};

export const canAdminEditPseudoMoMArtworkSlots = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin">,
  space: SpaceEditContract
): boolean => {
  return routeMode === "admin" && Boolean(viewerContext.admin) && isPseudoMoMSpace(space);
};

export const canAdminManageGallery = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin">
): boolean => routeMode === "admin" && Boolean(viewerContext.admin);

export const resolveCanManageGallerySettings = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin" | "canManageGallery">,
  space: SpaceEditContract
): boolean => {
  return Boolean(
    viewerContext.canManageGallery ||
    canAdminManageGallery(routeMode, viewerContext)
  );
};

export const resolveCanOpenGallerySetup = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin" | "canManageGallery">,
  space: SpaceEditContract
): boolean => {
  return resolveCanManageGallerySettings(routeMode, viewerContext, space);
};

export const resolveCanManageGalleryCollaborators = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin" | "isAdminAsSeller" | "collaboratorRole">,
  space: SpaceEditContract
): boolean => {
  return Boolean(
    viewerContext.collaboratorRole === "owner" ||
    viewerContext.isAdminAsSeller ||
    canAdminManageGallery(routeMode, viewerContext)
  );
};

export const resolveCanEditArtworkSlots = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin" | "personal" | "isAdminAsSeller">,
  space: SpaceEditContract
): boolean => {
  return Boolean(
    viewerContext.personal ||
    viewerContext.isAdminAsSeller ||
    canAdminEditPseudoMoMArtworkSlots(routeMode, viewerContext, space)
  );
};

export const resolveCanMoveArtworks = (
  routeMode: string | null | undefined,
  viewerContext: Pick<ViewerContext, "admin" | "personal" | "isAdminAsSeller">
): boolean => {
  return Boolean(
    viewerContext.personal ||
    viewerContext.isAdminAsSeller ||
    (routeMode === "admin" && viewerContext.admin)
  );
};

export type ArtworkSlotClickAction = "openListModal" | "openEditModal" | "openDetailsModal" | "none";

export const resolveArtworkSlotClickAction = (
  canEditArtworkSlots: boolean,
  canEditPseudoMoMArtworkSlots: boolean,
  hasArtworkWrapper: boolean,
  artworkCount: number,
  canUsePseudoMoMPillarActions: boolean = false
): ArtworkSlotClickAction => {
  if (canEditArtworkSlots && artworkCount <= 0) return "openListModal";
  if (canEditArtworkSlots && (canEditPseudoMoMArtworkSlots || canUsePseudoMoMPillarActions)) return "openEditModal";
  if (canEditArtworkSlots) return "openEditModal";
  if (hasArtworkWrapper && artworkCount > 0) return "openDetailsModal";
  return "none";
};

export const resolveClickedSlotIndex = (slot: any): number | null => {
  const directIndex = Number(slot?.index);
  if (Number.isFinite(directIndex)) return directIndex;

  const wrapperSlotTarget = Number(slot?.artworkWrapper?.slotTarget);
  if (Number.isFinite(wrapperSlotTarget)) return wrapperSlotTarget;

  return null;
};

export const resolveCollaboratorRole = (
  userId: string | undefined,
  galleryOwnerId: string | undefined,
  collaborators: CollaboratorEntry[] | undefined
): CollaboratorRole => {
  if (!userId || !galleryOwnerId) return null;
  if (userId === galleryOwnerId) return "owner";
  const entry = (collaborators || []).find((c) => c.userId === userId);
  return entry ? (entry.role as "editor" | "contributor") : null;
};

type GalleryCurrentUserResolution = {
  currentUser: any | null;
  errorMessage: string | null;
  shouldRecoverUnauthorized: boolean;
};

export const isAuthRequiredGalleryMode = (routeMode?: string | null): boolean => {
  return routeMode === "personal" || routeMode === "admin";
};

export const getGalleryRouteId = (pathname: string): string | null => {
  const match = pathname.match(/^\/gallery\/([^/?#]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const getGalleryRouteMode = (search: string): string | null => {
  try {
    return new URLSearchParams(search).get("t");
  } catch (_error) {
    return null;
  }
};

export const isOfflineEligiblePublicGalleryRoute = (
  pathname: string,
  search: string = ""
): boolean => {
  const routeMode = getGalleryRouteMode(search);
  return Boolean(getGalleryRouteId(pathname)) && !isAuthRequiredGalleryMode(routeMode);
};

export const shouldBypassAutoLoginForOfflineGallery = (
  pathname: string,
  search: string,
  isOnline: boolean
): boolean => {
  return !isOnline && isOfflineEligiblePublicGalleryRoute(pathname, search);
};

export const shouldSkipGalleryCurrentUserLookup = (
  routeMode: string | null | undefined,
  isOnline: boolean,
  isOfflineEligibleRoute: boolean,
  hasAccessToken = true
): boolean => {
  if (isAuthRequiredGalleryMode(routeMode)) return false;
  if (!isOnline && isOfflineEligibleRoute) return true;
  return !hasAccessToken;
};

export const canUseCachedPublicGalleryReplay = (
  cachedGallery: CachedPublicGalleryContract,
  requestedGalleryId: string | null | undefined,
  isOfflineEligibleRoute: boolean
): boolean => {
  return Boolean(
    isOfflineEligibleRoute &&
    requestedGalleryId &&
    cachedGallery?.galleryId === requestedGalleryId
  );
};

export const shouldUseCachedPublicGalleryArtworks = (
  artworksResponse: { status?: boolean } | null | undefined,
  isCachedPublicReplay: boolean
): boolean => {
  return Boolean(isCachedPublicReplay && !artworksResponse?.status);
};

export const shouldShowOfflineUnavailableForBootstrapFailure = (
  response: any,
  isOnline: boolean,
  isOfflineEligibleRoute: boolean
): boolean => {
  return Boolean(isOfflineEligibleRoute && !response?.status && (!isOnline || response?.networkError));
};

export const collectGalleryArtworksFromWrappers = <TArtwork>(
  artworkWrappers: Array<{ artworks?: TArtwork[] | null } | null | undefined> | null | undefined
): TArtwork[] => {
  return (artworkWrappers || []).flatMap((wrapper) => wrapper?.artworks || []);
};

export const hasPlatformAdminRole = (roles: unknown): boolean => {
  return Array.isArray(roles) && roles.some((role) => PLATFORM_ADMIN_ROLES.includes(String(role)));
};

export const resolveGalleryViewerContext = (
  routeMode: string | null | undefined,
  user: any,
  galleryOwnerId: string,
  collaborators?: CollaboratorEntry[]
): ViewerContext => {
  if (!user && isAuthRequiredGalleryMode(routeMode)) {
    throw new Error("Authentication required to load this gallery mode.");
  }

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isSeller = roles.includes("SELLER");
  const isAdmin = hasPlatformAdminRole(roles);
  const collaboratorRole = resolveCollaboratorRole(user?._id, galleryOwnerId, collaborators);

  if (routeMode === "admin") {
    return {
      admin: isAdmin,
      isAdminAsSeller: false,
      personal: false,
      canManageGallery: false,
      collaboratorRole: null,
    };
  }

  if (routeMode === "personal") {
    const isAdminAsSeller = Boolean(isAdmin && user?._id && galleryOwnerId !== user._id);
    const isOwner = Boolean(isSeller && user?._id && galleryOwnerId === user._id);
    const isEditorOrContributor = collaboratorRole === "editor" || collaboratorRole === "contributor";
    const personal = isOwner || isAdminAsSeller || isEditorOrContributor;
    const canManageGallery = isOwner || isAdminAsSeller || collaboratorRole === "editor";

    return {
      admin: false,
      isAdminAsSeller,
      personal,
      canManageGallery,
      collaboratorRole: isOwner ? "owner" : collaboratorRole,
    };
  }

  return {
    admin: false,
    isAdminAsSeller: false,
    personal: false,
    canManageGallery: false,
    collaboratorRole: null,
  };
};

export const resolveGalleryCurrentUser = (
  routeMode: string | null | undefined,
  meResponse: any
): GalleryCurrentUserResolution => {
  if (meResponse?.status && meResponse.user) {
    return {
      currentUser: meResponse.user,
      errorMessage: null,
      shouldRecoverUnauthorized: false,
    };
  }

  if (!isAuthRequiredGalleryMode(routeMode)) {
    return {
      currentUser: null,
      errorMessage: null,
      shouldRecoverUnauthorized: false,
    };
  }

  if (Number(meResponse?.statusCode) === 401) {
    return {
      currentUser: null,
      errorMessage: null,
      shouldRecoverUnauthorized: true,
    };
  }

  return {
    currentUser: null,
    errorMessage: "Failed to load current user.",
    shouldRecoverUnauthorized: false,
  };
};

export const ensureGalleryBootstrapStep = <T>(
  response: any,
  errorMessage: string,
  selector: (response: any) => T | null | undefined
): T => {
  if (!response?.status) {
    throw new Error(errorMessage);
  }

  const value = selector(response);
  if (value === null || value === undefined) {
    throw new Error(errorMessage);
  }

  return value;
};
